// ── AssignmentPanel ───────────────────────────────────────────────────────────
// Shows the active assignment for a control with state badge, actor info,
// transition buttons, and collapsible audit history.
//
// D.2 additions:
// - effectiveTier + scopeAssignment props: surfaces inherited context
// - When effective tier is 'category' or 'framework' and no L3 exists, shows
//   the inherited assignment with "Override for this control" CTA.
// - scope prop is 'control' (default) — kept additive for future L1/L2 use.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils';
import { SubmitWithEvidenceModal } from '@/components/compliance/SubmitWithEvidenceModal';
import { useAssignments, useAssignment, useDeleteAssignment, useCreateAssignment } from './api';
import { AssignModal } from './AssignModal';
import { LEGACY_ASSIGN_MOVED } from './legacy-neutralized';
import { ReassignModal } from './ReassignModal';
import { StateTransitionButtons } from './StateTransitionButtons';
import { ApproveModal, SentBackModal } from './VerifierModals';
import { STATUS_LABELS } from '@/features/compliance/statusLabels';
import type { Assignment, AssignmentState, InheritanceTier, ScopeAssignment } from '@grc/shared';

// ── State badge ───────────────────────────────────────────────────────────────

type BadgeVariant = 'default' | 'emerald' | 'amber' | 'rose' | 'sky' | 'violet' | 'muted' | 'outline';

// ED-SH-5: colour palette — working-early grey, working-active blue, sent-back amber,
// terminal-covered green, terminal-scoped-out slate/muted.
const STATUS_VARIANTS: Record<AssignmentState, BadgeVariant> = {
  unassigned:           'muted',
  assigned:             'muted',
  in_progress:          'sky',
  submitted:            'violet',
  sent_back:            'amber',
  implemented:          'emerald',
  compensating_control: 'emerald',
  not_applicable:       'muted',
};

function StateBadge({ status }: { status: AssignmentState }) {
  return <Badge variant={STATUS_VARIANTS[status]}>{STATUS_LABELS[status]}</Badge>;
}

// ── Assignee display ───────────────────────────────────────────────────────────

function AssigneeDisplay({ assignment }: { assignment: Assignment }) {
  const isSameUser =
    assignment.assignee_user_id !== null &&
    assignment.assignee_user_id === assignment.verifier_user_id;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-text-secondary w-16 shrink-0">Assignee</span>
        <span className="text-xs text-text-primary font-medium">
          {assignment.assignee_display_name ?? assignment.assignee_email ?? '—'}
        </span>
        {assignment.assignee_is_pending && (
          <Badge variant="amber" className="text-[9px] py-0">Pending</Badge>
        )}
      </div>
      {assignment.verifier_user_id && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-text-secondary w-16 shrink-0">Verifier</span>
          <span className="text-xs text-text-primary font-medium">
            {assignment.verifier_display_name ?? assignment.verifier_email ?? '—'}
          </span>
        </div>
      )}
      {isSameUser && (
        <div className="flex items-center gap-1.5 rounded-md bg-accent-amber/10 border border-accent-amber/30 px-2.5 py-1.5 text-[10px] text-accent-amber">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          Assignee and verifier are the same person — tenant audit maturity permitting.
        </div>
      )}
      {assignment.due_at && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-secondary w-16 shrink-0">Due</span>
          <span className="text-xs text-text-primary">{new Date(assignment.due_at).toLocaleDateString()}</span>
        </div>
      )}
    </div>
  );
}

// ── Audit history row ─────────────────────────────────────────────────────────

interface AuditEntry {
  id: string;
  event_type: string;
  actor_type: string;
  user_id: string | null;
  occurred_at: string;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
}

function HistoryEntry({ entry }: { entry: AuditEntry }) {
  const label = entry.event_type.replace('assignment.', '').replace(/_/g, ' ');
  const toStatus = entry.after_state?.status as AssignmentState | undefined;
  const sentBackComment = entry.metadata?.sent_back_comment as string | undefined;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-border-subtle last:border-0">
      <div className="flex-1 min-w-0">
        <span className="text-[10px] font-medium text-text-primary capitalize">{label}</span>
        {toStatus && (
          <span className="text-[10px] text-text-secondary ml-1">
            → {STATUS_LABELS[toStatus] ?? toStatus.replace(/_/g, ' ')}
          </span>
        )}
        {sentBackComment && (
          <p className="text-[10px] text-accent-amber mt-0.5 italic">"{sentBackComment}"</p>
        )}
        {entry.metadata?.note != null && (
          <p className="text-[10px] text-text-secondary mt-0.5 italic line-clamp-2">{String(entry.metadata.note)}</p>
        )}
      </div>
      <span className="text-[10px] text-text-secondary whitespace-nowrap shrink-0">
        {new Date(entry.occurred_at).toLocaleString()}
      </span>
    </div>
  );
}

// ── Assignment detail view ────────────────────────────────────────────────────

function AssignmentDetail({
  assignmentId,
  controlId,
  frameworkId,
  isCiso,
  currentUserId,
}: {
  assignmentId: string;
  controlId: string;
  frameworkId?: string;
  isCiso: boolean;
  currentUserId: string;
}) {
  const { data, isLoading } = useAssignment(assignmentId);
  const deleteMutation = useDeleteAssignment(controlId, frameworkId);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [sentBackOpen, setSentBackOpen] = useState(false);

  if (isLoading) return <Skeleton className="h-24 w-full" />;
  if (!data) return null;

  const assignment = data;
  const history = (data as Assignment & { history: AuditEntry[] }).history ?? [];

  function handleDelete() {
    if (!window.confirm('Delete this assignment? This cannot be undone.')) return;
    deleteMutation.mutate(assignment.id, {
      onSuccess: () => toast.success('Assignment deleted'),
      onError: () => toast.error('Cannot delete — assignment may need to be closed first'),
    });
  }

  return (
    <div className="space-y-3 rounded-lg border border-border-subtle bg-bg-surface-2 p-3">
      <div className="flex items-center justify-between gap-2">
        <StateBadge status={assignment.status} />
        <div className="flex items-center gap-1.5">
          {isCiso && (
            <>
              <Button
                variant="ghost" size="sm" className="text-xs h-6 px-2"
                disabled title={LEGACY_ASSIGN_MOVED}
                onClick={() => setReassignOpen(true)}
              >
                Reassign
              </Button>
              {assignment.status === 'assigned' && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-6 px-2 text-accent-rose hover:text-accent-rose"
                  onClick={handleDelete}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <AssigneeDisplay assignment={assignment} />

      {/* AC-H-6-3: Show sent-back comment prominently so assignee sees it immediately */}
      {assignment.status === 'sent_back' && assignment.note && (
        <div className="flex items-start gap-2 rounded-md border border-accent-amber/40 bg-accent-amber/10 px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 text-accent-amber shrink-0 mt-0.5" />
          <div>
            <p className="text-[10px] font-semibold text-accent-amber mb-0.5">Sent back — verifier comment</p>
            <p className="text-xs text-text-primary">{assignment.note}</p>
          </div>
        </div>
      )}

      {assignment.last_transitioned_at && (
        <p className="text-[10px] text-text-secondary">
          Last updated {new Date(assignment.last_transitioned_at).toLocaleString()}
          {assignment.last_transitioned_by_name && ` by ${assignment.last_transitioned_by_name}`}
        </p>
      )}

      {/* Reminder status (read-only, Sprint D.3) */}
      {assignment.reminders_disabled_at ? (
        <p className="text-[10px] text-text-secondary">
          Reminders disabled ({new Date(assignment.reminders_disabled_at).toLocaleDateString()})
        </p>
      ) : assignment.next_reminder_at ? (
        <p className="text-[10px] text-text-secondary">
          Next reminder: {new Date(assignment.next_reminder_at).toLocaleDateString()}
          {assignment.reminder_count ? ` · ${assignment.reminder_count} sent` : ''}
        </p>
      ) : null}

      <StateTransitionButtons
        assignment={assignment}
        currentUserId={currentUserId}
        isCiso={isCiso}
        controlId={controlId}
        onBeforeSubmit={() => setSubmitModalOpen(true)}
        onBeforeApprove={() => setApproveOpen(true)}
        onBeforeSentBack={() => setSentBackOpen(true)}
      />

      {history.length > 0 && (
        <div className="pt-1">
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="flex items-center gap-1 text-[10px] text-text-secondary hover:text-text-primary"
          >
            {historyOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {historyOpen ? 'Hide' : 'Show'} history ({history.length})
          </button>
          {historyOpen && (
            <div className="mt-2 space-y-0">
              {history.map((entry: AuditEntry) => (
                <HistoryEntry key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </div>
      )}

      {reassignOpen && (
        <ReassignModal
          assignment={assignment}
          controlId={controlId}
          open={reassignOpen}
          onClose={() => setReassignOpen(false)}
        />
      )}

      <SubmitWithEvidenceModal
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        assignment={assignment}
        controlId={controlId}
      />

      <ApproveModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        assignment={assignment}
        controlId={controlId}
      />

      <SentBackModal
        open={sentBackOpen}
        onClose={() => setSentBackOpen(false)}
        assignment={assignment}
        controlId={controlId}
      />
    </div>
  );
}

// ── Inherited assignment display ──────────────────────────────────────────────

function InheritedDisplay({
  tier,
  scopeAssignment,
  controlId,
  isCiso,
}: {
  tier: 'category' | 'framework';
  scopeAssignment: ScopeAssignment;
  controlId: string;
  isCiso: boolean;
}) {
  const createMutation = useCreateAssignment(controlId);
  const [overriding, setOverriding] = useState(false);
  // ⚠ Item 1 neutralized the legacy assign WRITES (see ./legacy-neutralized). The original
  //   disabled-conditions below are kept in the expression rather than deleted: the state they
  //   read belongs to a machine TC6 retires, and half-removing it here would leave a mutation
  //   wired to nothing with no trace of why.
  const legacyAssignWriteDisabled = true;

  const source = tier === 'category'
    ? `category: ${scopeAssignment.category}`
    : 'framework';

  function handleOverride() {
    setOverriding(true);
    // Pre-fill with inherited values so CISO can confirm
    createMutation.mutate(
      {
        control_id: controlId,
        assignee_user_id: scopeAssignment.assignee_user_id ?? undefined,
        assignee_email: scopeAssignment.assignee_user_id ? undefined : (scopeAssignment.assignee_email ?? undefined),
        verifier_user_id: scopeAssignment.verifier_user_id ?? undefined,
      },
      {
        onSuccess: () => { toast.success('Explicit assignment created for this control'); setOverriding(false); },
        onError: () => { toast.error('Override failed'); setOverriding(false); },
      },
    );
  }

  return (
    <div className="space-y-2 rounded-lg border border-border-subtle bg-bg-surface-2 p-3">
      <div className="flex items-center gap-2">
        <Badge variant="sky" className="text-[9px]">Inherited: {tier === 'category' ? 'Category' : 'Framework'}</Badge>
      </div>
      <p className="text-[10px] text-text-secondary">
        Inherited from {source}
      </p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-text-secondary w-16 shrink-0">Assignee</span>
        <span className="text-xs text-text-primary font-medium">
          {scopeAssignment.assignee_display_name ?? scopeAssignment.assignee_email ?? '—'}
        </span>
        {scopeAssignment.assignee_is_pending && (
          <Badge variant="amber" className="text-[9px] py-0">Pending</Badge>
        )}
      </div>
      {isCiso && (
        <Button
          variant="outline"
          size="sm"
          className="w-full text-xs mt-1"
          onClick={handleOverride}
          disabled={legacyAssignWriteDisabled || overriding || createMutation.isPending}
          title={LEGACY_ASSIGN_MOVED}
        >
          <ArrowRight className="h-3 w-3 mr-1" />
          Override for this control
        </Button>
      )}
    </div>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────

interface AssignmentPanelProps {
  controlId: string;
  /** D.2: effective tier from parent (avoids re-fetching) */
  frameworkId?: string;
  effectiveTier?: InheritanceTier;
  scopeAssignment?: ScopeAssignment | null;
  layout?: 'side-panel' | 'page';
}

export function AssignmentPanel({
  controlId,
  frameworkId,
  effectiveTier = 'none',
  scopeAssignment = null,
  layout = 'side-panel',
}: AssignmentPanelProps) {
  const { user, hasAnyRole } = useAuthStore();
  const isCiso = hasAnyRole(['risk_manager', 'super_admin']);
  const currentUserId = user?.id ?? '';

  const { data: assignments, isLoading } = useAssignments(controlId);
  const [assignOpen, setAssignOpen] = useState(false);

  const list = assignments ?? [];
  const hasL3 = list.length > 0;

  return (
    <div className={cn('space-y-3', layout === 'page' && 'space-y-4')}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Assignments</p>
        <div className="flex items-center gap-2">
          {layout === 'side-panel' && frameworkId && (
            <Link
              to={`/compliance/controls/${frameworkId}/${controlId}`}
              className="text-xs text-text-secondary hover:text-text-primary transition-colors"
            >
              Open detail page →
            </Link>
          )}
          {isCiso && (
            <Button
              variant="outline" size="sm" className="text-xs"
              disabled title={LEGACY_ASSIGN_MOVED}
              onClick={() => setAssignOpen(true)}
            >
              + Assign
            </Button>
          )}
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-20 w-full" />
      ) : hasL3 ? (
        <div className="space-y-3">
          {list.map((a) => (
            <AssignmentDetail
              key={a.id}
              assignmentId={a.id}
              controlId={controlId}
              frameworkId={frameworkId}
              isCiso={isCiso}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      ) : (effectiveTier === 'category' || effectiveTier === 'framework') && scopeAssignment ? (
        /* No L3 — show inherited assignment with override CTA */
        <InheritedDisplay
          tier={effectiveTier}
          scopeAssignment={scopeAssignment}
          controlId={controlId}
          isCiso={isCiso}
        />
      ) : (
        <p className="text-xs text-text-secondary italic">No assignments for this control</p>
      )}

      {assignOpen && (
        <AssignModal
          controlId={controlId}
          open={assignOpen}
          onClose={() => setAssignOpen(false)}
        />
      )}
    </div>
  );
}
